import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { lookupAPI, referralAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from './ContentHeader';
import ReferralInsuranceWorkspaceSection from './ReferralInsuranceWorkspaceSection';
import './ClientReferralInsurancePage.css';

const PAGE_TITLE = 'Insurance';
const PAGE_TITLE_HINT =
  'Same insurance workspace as on the referral form—useful when the service coordinator is reviewing or updating coverage, waivers, or exemption without opening the full referral.';

/**
 * Slim client-scoped route: edit referral insurance workspace (coverage, waivers, exemptions)
 * without opening the full referral form. Same APIs as ReferralForm insurance block.
 */
function ClientReferralInsurancePage() {
  const { id: clientId, referralId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();

  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [referral, setReferral] = useState(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  const insuranceWorkspaceSaveRef = useRef(null);
  const registerInsuranceWorkspaceSave = useCallback((fn) => {
    insuranceWorkspaceSaveRef.current = fn;
  }, []);

  const canUpdateReferral = hasPermission('referrals.update');
  const canReadReferral = hasPermission('referrals.read');
  const canManageInsuranceCompaniesLookup =
    hasPermission('settings.view') && hasPermission('settings.manage_lookups');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!clientId || !referralId) {
        setPageError('Missing client or referral.');
        setPageLoading(false);
        return;
      }
      if (!canReadReferral) {
        setPageError('You do not have permission to view this referral.');
        setPageLoading(false);
        return;
      }

      setPageLoading(true);
      setPageError(null);
      try {
        const [refRes, companiesRes, typesRes] = await Promise.all([
          referralAPI.getById(referralId),
          lookupAPI.getInsuranceCompanies(),
          lookupAPI.getInsuranceTypes(),
        ]);
        if (cancelled) return;

        const ref = refRes.data?.data;
        if (!ref || String(ref.client_id) !== String(clientId)) {
          setPageError('This referral was not found for this client.');
          setReferral(null);
          return;
        }

        setReferral(ref);
        setInsuranceCompanies(companiesRes.data?.data || []);
        setInsuranceTypes(typesRes.data?.data || []);
      } catch (err) {
        console.error(err);
        setPageError(err.response?.data?.error || err.message || 'Failed to load page.');
        setReferral(null);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, referralId, canReadReferral]);

  const goBackToClient = useCallback(() => {
    navigate(tenantPath(`/client/${clientId}`));
  }, [navigate, tenantPath, clientId]);

  const handleSave = async () => {
    if (!insuranceWorkspaceSaveRef.current) return;
    setSaving(true);
    try {
      await insuranceWorkspaceSaveRef.current();
      navigate(tenantPath(`/client/${clientId}`), { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to save insurance';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const isClosed = !!referral?.close_date;
  const readOnly = !canUpdateReferral || isClosed;

  const saveCancelBar = (position) =>
    !readOnly && (
      <div
        className={`client-referral-insurance-actions client-referral-insurance-actions--${position}`}
      >
        <button type="button" className="btn-cancel" onClick={goBackToClient} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn-save" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    );

  if (pageLoading) {
    return (
      <div className="client-referral-insurance-page">
        <ContentHeader title={PAGE_TITLE} />
        <p className="client-referral-insurance-loading">Loading…</p>
      </div>
    );
  }

  if (pageError || !referral) {
    return (
      <div className="client-referral-insurance-page">
        <ContentHeader title={PAGE_TITLE} />
        <div className="client-referral-insurance-error" role="alert">
          {pageError || 'Unable to load this referral.'}
        </div>
        <button type="button" className="btn-cancel client-referral-insurance-back-only" onClick={goBackToClient}>
          Back to client
        </button>
      </div>
    );
  }

  return (
    <div className="client-referral-insurance-page">
      <ContentHeader title={PAGE_TITLE} titleHint={PAGE_TITLE_HINT} />
      {isClosed || !canUpdateReferral ? (
        <p className="client-referral-insurance-subtitle">
          {isClosed ? <span className="client-referral-insurance-closed">Closed referral (read-only)</span> : null}
          {!canUpdateReferral && !isClosed ? (
            <span className="client-referral-insurance-closed">View only (no referral update permission)</span>
          ) : null}
        </p>
      ) : null}
      <p className="client-referral-insurance-meta">
        <button type="button" className="client-referral-insurance-back-link" onClick={goBackToClient}>
          ← Back to client
        </button>
      </p>

      {saveCancelBar('top')}

      <ReferralInsuranceWorkspaceSection
        referralId={referralId}
        clientId={clientId}
        insuranceCompanies={insuranceCompanies}
        insuranceTypes={insuranceTypes}
        readOnly={readOnly}
        registerSaveHandler={readOnly ? undefined : registerInsuranceWorkspaceSave}
        helpPersistMessage="Use Save (top or bottom) to keep insurance changes on this page."
        canManageInsuranceCompaniesLookup={canManageInsuranceCompaniesLookup}
        referralFormPathForInsuranceLookup={`${location.pathname}${location.search || ''}`}
      />

      {saveCancelBar('bottom')}
    </div>
  );
}

export default ClientReferralInsurancePage;
